import React, { useMemo } from 'react';
import cn from 'classnames';
import Svg from '../Svg';
import useDesignStatus from './useDesignStatus';
import { additionalHoursBought, isConsultancy, isRoomStyling } from '../ProjectStatus/utils';

const STATUSES = [
  { label: 'Complete<br />brief', value: 'complete_brief' },
  { label: 'Video call to discuss brief <br /> and design direction', value: 'video_call' },
  {
    label: project =>
      project.plan === 'mini' ||
      (project.questionnaire && isRoomStyling(project.questionnaire.room_type))
        ? 'Agree design<br />direction'
        : 'Video call to agree room layout & design direction',
    value: 'agree_design_direction_and_room_layout',
  },
  { label: 'Designer<br />sourcing items', value: 'designer_sourcing_item' },
  {
    label: 'Final design & shopping<br />list unlocked',
    hint: project => `${project.plan === 'mini' ? 1 : 2} weeks post<br />designer support starts`,
    value: 'final_design',
    disabled: true,
  },
  { label: 'Shop design<br />(with exclusive discounts)', value: 'shop_design', disabled: true },
];

const DesignStatus = ({
  project,
  editable,
  info = true,
  statusHeader = true,
  className,
  children,
  customer = false,
  customerView,
}) => {
  const { status: designStatus, update } = useDesignStatus(project.id);
  const status = designStatus || project.design_process_status;
  const consultancy = isConsultancy(project.plan);
  const additionalHours = additionalHoursBought(project.additional_hours_status);
  const items = useMemo(
    () => {
      const activeIndex = STATUSES.map(s => s.value).indexOf(status);
      return STATUSES.map((s, idx) => {
        const active = idx === activeIndex;
        const disabled = s.disabled ? s.disabled : idx === 0 ? false : idx > activeIndex + 1;
        let icon = '';
        if (s.value === 'final_design') {
          icon = active ? 'unlock' : 'lock';
        } else {
          icon = active || idx < activeIndex ? 'check' : '';
        }
        return {
          ...s,
          label: typeof s.label === 'function' ? s.label(project) : s.label,
          active,
          icon,
          selected: active || idx < activeIndex,
          disabled: editable ? disabled : true,
        };
      });
    },
    [status, editable],
  );

  return (
    <div
      className={cn('design-status', className, { '-design-status_customer-view': customerView })}
    >
      <div className="design-status_wrapper">
        {info && (
          <div
            className={cn('design-status_info', {
              '-pro': project.is_pro_platform,
            })}
          >
            <h2>{project.name}</h2>
            {project.plan_name && (
              <div className="design-status_text">
                {/*Package: {project.plan_name}*/}
                {project.questionnaire.term === 'express' ? ', Express' : ''}
              </div>
            )}

            {project.designer_full_name && (
              <div className="design-status_text">Designer: {project.designer_full_name}</div>
            )}
            {!customer && (
              <div className="design-status_text">
                Customer:
                {project.customer_full_name}
              </div>
            )}
            {additionalHours && (
              <div className="design-status_text">
                Hours bought: {project.hours_purchased} <br />
                Hours complete: {project.hours_used}
              </div>
            )}
          </div>
        )}
        {!project.is_pro_platform &&
          !consultancy && (
            <div className="design-status_pipeline">
              {statusHeader && <h2 className="design-status_heading">Project Process</h2>}
              <div className="design-status_items">
                {items.map((status, index) => (
                  <div
                    key={status.value}
                    className={cn('design-status_item', {
                      '-active': status.selected,
                      '-disabled': status.disabled,
                    })}
                    onClick={() => !status.disabled && update(status.value)}
                  >
                    <div
                      className={cn('design-status_title', {
                        '-under': index % 2 === 1,
                        '-active': status.selected,
                      })}
                    >
                      <div dangerouslySetInnerHTML={{ __html: status.label }} />
                      {status.hint &&
                        (status.selected ? (
                          <a
                            href={`/projects/${project.id}/moodboard`}
                            className="design-status_hint"
                            dangerouslySetInnerHTML={{ __html: status.hint(project) }}
                          />
                        ) : (
                          <div
                            className="design-status_hint"
                            dangerouslySetInnerHTML={{ __html: status.hint(project) }}
                          />
                        ))}
                    </div>
                    <div className="design-status_line">
                      <div className="design-status_point">
                        {status.icon && (
                          <Svg
                            icon={`icon-${status.icon}`}
                            className={cn('design-status_icon', `-${status.icon}`)}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
      </div>
      {children}
    </div>
  );
};

export default DesignStatus;



// WEBPACK FOOTER //
// ./app/javascript/common/components/DesignStatus/DesignStatus.js